<template>
  <div class="page-container">
    <!-- 工具条 -->
    <div class="toolbar">
      <div class="left">
        <el-button round @click="fetchList" :loading="loading">
          <el-icon>
            <Refresh />
          </el-icon> 刷新
        </el-button>

        <el-input v-model="search.keyword" placeholder="按任务名/视频任务ID 搜索" clearable style="width: 300px"
          @keyup.enter="doSearch" class="ml10">
          <template #prefix><el-icon>
              <Search />
            </el-icon></template>
        </el-input>
        <el-button type="primary" class="ml10" @click="doSearch">
          <el-icon>
            <Search />
          </el-icon> 搜索
        </el-button>

        <!-- 批量删除 -->
        <el-popconfirm v-if="selectedIds.length" :title="`确认删除选中的 ${selectedIds.length} 条记录吗？`"
          @confirm="doBatchDelete">
          <template #reference>
            <el-button type="danger" class="ml10" :disabled="!selectedIds.length">
              批量删除<span v-if="selectedIds.length">（{{ selectedIds.length }}）</span>
            </el-button>
          </template>
        </el-popconfirm>
      </div>

      <div class="right">
        <el-button class="mr8"
          :disabled="!selectedIds.length || !list.some(r => selectedIds.includes(r.id) && r.human_synthetic_video_url)"
          :loading="zipMaking" @click="downloadBatchZip">
          <el-icon>
            <Download />
          </el-icon>
          批量打包下载ZIP

        </el-button>

        <el-button class="mr8" :loading="refreshingBatch" @click="refreshPending">
          <el-icon>
            <Refresh />
          </el-icon> 批量刷新进行中（{{ pendingCount }}）
        </el-button>


        <el-button type="success" round @click="openAdd">
          <el-icon>
            <Plus />
          </el-icon> 添加
        </el-button>
      </div>
    </div>

    <el-dialog v-model="preview.visible" :title="preview.row?.human_task_name || '视频预览'" width="720px"
      :close-on-click-modal="true" destroy-on-close>
      <div v-if="preview.row" class="preview-wrap">
        <video :src="preview.row.human_synthetic_video_url" controls preload="metadata" class="preview-video"></video>
        <div class="preview-meta">
          <div>视频任务ID：{{ preview.row.video_task_id }}</div>
          <div v-if="preview.row.duration_ms">时长：{{ Math.ceil(preview.row.duration_ms / 1000) }}s</div>
        </div>
      </div>
    </el-dialog>


    <!-- 列表 -->
    <el-table :data="list" border stripe v-loading="loading" style="width: 100%" :row-key="row => row.id"
      height="calc(100vh - 260px)" @selection-change="handleSelectionChange">

      <el-table-column type="selection" width="48" />
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="human_task_name" label="任务名" min-width="220" show-overflow-tooltip />
      <el-table-column prop="human_clone_id" label="形象ID(scene_task_id)" min-width="200" show-overflow-tooltip />
      <el-table-column label="通道" width="90">
        <template #default="s">
          <el-tag size="small" :type="channelTagType(s.row.generate_channel)">
            {{ channelLabel(s.row.generate_channel) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="音频" min-width="320">
        <template #default="s">
          <div class="audio-cell" v-if="s.row.voice_url">
            <audio :src="s.row.voice_url" controls preload="none" class="audio-el"></audio>

            <el-button link type="primary" @click="copyText(s.row.voice_url)">复制</el-button>
          </div>
          <span v-else class="muted">—</span>
        </template>
      </el-table-column>
      <el-table-column label="视频" min-width="250">
        <template #default="s">
          <div v-if="s.row.human_synthetic_video_url" class="video-cell-lite">
            <el-image v-if="s.row.human_synthetic_cover_url" :src="s.row.human_synthetic_cover_url" fit="cover" lazy
              class="thumb" />
            <div v-else class="thumb thumb--placeholder">无封面</div>

            <div class="v-actions">
              <el-button size="small" @click="openPreview(s.row)">预览</el-button>
              <el-button link type="primary" @click="copyText(s.row.human_synthetic_video_url)">复制</el-button>
            </div>
          </div>
          <span v-else class="muted">—</span>
        </template>
      </el-table-column>

      <el-table-column label="状态" width="100">
        <template #default="s">
          <el-tag :type="s.row.status === 1 ? 'success' : s.row.status === 3 ? 'warning' : 'danger'">
            {{ s.row.status === 1 ? '成功' : s.row.status === 3 ? '处理中' : '失败' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="video_task_id" label="视频任务ID" min-width="160" show-overflow-tooltip />
      <el-table-column prop="create_time" label="创建时间" width="180" />
      <el-table-column label="操作" width="180" fixed="right">
        <template #default="s">
          <el-button v-if="s.row.human_synthetic_video_url" type="primary" size="small" @click="downloadOne(s.row)">
            下载
          </el-button>

          <el-button v-if="s.row.status === 3 && s.row.video_task_id" size="small"
            :loading="rowRefreshingId === s.row.id" @click="refreshOne(s.row)">
            <el-icon>
              <Refresh />
            </el-icon> 刷新
          </el-button>
          <el-button type="danger" size="small" @click="delOne(s.row.id)">
            <el-icon>
              <Delete />
            </el-icon> 删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pager">
      <el-pagination v-model:current-page="pager.page" v-model:page-size="pager.pageSize" :total="pager.total"
        :page-sizes="[10, 20, 30, 50]" layout="total, sizes, prev, pager, next, jumper" @size-change="fetchList"
        @current-change="fetchList" />
    </div>

    <!-- 新增弹窗 -->
    <el-dialog v-model="dialogVisible" title="形象视频合成" width="880px" :close-on-click-modal="false">
      <el-form ref="formRef" :model="form" :rules="rules" label-width="110px">
        <!-- 任务名：支持中/英逗号分割 -->
        <el-form-item label="任务名" prop="human_task_name">
          <el-input v-model="form.human_task_name" placeholder="支持多个，用中/英逗号分隔">
            <template #append>
              <el-button @click="insertComma('human_task_name')" text>插入分隔符</el-button>
            </template>
          </el-input>
          <div class="hint">多个任务名会按顺序与素材/形象组合</div>
        </el-form-item>

        <!-- 形象（场景）多选 -->
        <el-form-item label="选择形象" prop="human_clone_id">
          <el-select v-model="form.human_clone_id" multiple filterable collapse-tags collapse-tags-tooltip
            placeholder="可多选；scene_task_id 唯一" style="width: 100%">
            <el-option v-for="item in figures" :key="item.scene_task_id"
              :label="`${item.human_name}（${item.scene_task_id}）`" :value="item.scene_task_id" />
          </el-select>
        </el-form-item>

        <!-- 音频来源 -->
        <el-form-item label="音频来源">
          <el-radio-group v-model="form.voice_type">
            <el-radio :label="1">本地/直链</el-radio>
            <el-radio :label="2">历史音频</el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- 本地/直链 -->
        <template v-if="form.voice_type === 1">
          <el-form-item label="音频直链">
            <el-input v-model="manualVoiceUrls" type="textarea" :rows="4"
              placeholder="每行一个，或用英文逗号 , 分隔；上传成功的链接会自动填充到此处" />
          </el-form-item>
          <el-form-item label="上传音频">
            <div class="inline-actions">
              <el-upload class="drop-audio" drag multiple :auto-upload="false" :show-file-list="false"
                accept=".mp3,.wav,.m4a,.aac" :on-change="onPickLocalAudio">
                <div class="drop-inner">
                  <el-icon>
                    <Upload />
                  </el-icon>
                  <div class="drop-title">将音频拖到此处</div>
                  <div class="drop-sub">或 <span class="link">点击选择</span>（可多选）</div>
                  <div class="drop-meta">建议时长 ≥ 3 秒</div>
                </div>
              </el-upload>

              <div v-if="uploadQueue.length" class="queue">
                <div class="queue-title">上传进度</div>
                <div v-for="q in uploadQueue" :key="q.id" class="queue-item">
                  <span class="q-name" :title="q.name">{{ q.name }}</span>
                  <el-progress :percentage="q.percent" :status="q.status" />
                </div>
              </div>

              <div v-if="voiceUrlList.length" class="url-chips">
                <el-tag v-for="(u, i) in voiceUrlList" :key="u" closable @close="removeVoiceUrl(i)" effect="plain">
                  {{ u }} <span v-if="durMap[u]">（{{ Math.ceil((durMap[u] || 0) / 1000) }}s）</span>
                </el-tag>
              </div>
            </div>
          </el-form-item>
        </template>

        <!-- 历史音频（来自 voice_clone_synthetic_record.php） -->
        <template v-else>
          <el-form-item label="历史音频" prop="history_voice_ids">
            <el-select v-model="selectedHistoryIds" multiple filterable collapse-tags collapse-tags-tooltip
              placeholder="选择历史已合成音频（支持多选）" style="width: 100%">
              <el-option v-for="r in historyAudios" :key="r.id" :label="`${r.voice_task_name || '未命名'}（#${r.id}）`"
                :value="r.id">
                <div class="opt-line">
                  <span class="opt-name">{{ r.voice_task_name || '未命名' }}</span>
                  <span class="opt-sub">#{{ r.id }}</span>
                </div>
                <div class="opt-sub" style="max-width:520px;overflow:hidden;text-overflow:ellipsis;">
                  {{ r.human_voice_synthetic_url }}
                </div>
                <!-- ✅ 新增：展示时长（向上取整秒） -->
                <div class="opt-sub">时长：{{ durDisplay(r.human_voice_synthetic_url) }}</div>
              </el-option>
            </el-select>

            <!-- 已选标签内也展示时长（向上取整秒） -->
            <div v-if="selectedHistoryIds.length" class="url-chips mt6">
              <el-tag v-for="hid in selectedHistoryIds" :key="hid" closable @close="removeHistoryId(hid)"
                effect="plain">
                {{ findHistoryLabel(hid) }}
                <span class="opt-sub">（{{ durDisplay(historyUrlById(hid)) }}）</span>
              </el-tag>
            </div>

            <div class="hint mt6">
              首次选择历史音频时将自动拉取时长用于预估；实际扣点以平台返回为准。
            </div>
          </el-form-item>
        </template>

        <!-- 通道与价格 -->
        <el-form-item label="生成通道">
          <el-select v-model="form.generate_channel" style="width:260px">
            <el-option :value="1" label="普通通道" />
            <el-option :value="2" label="PRO 通道" />
            <el-option :value="3" label="极速通道" />
          </el-select>
          <div class="hint mt6">
            单价（每秒）：普通 {{ price.normal }}/s；PRO {{ price.pro }}/s；极速 {{ price.turbo }}/s
          </div>
          <el-alert :title="priceTip" type="info" :closable="false" show-icon class="mt6" />
        </el-form-item>

        <!-- 策略：全员合成 -->
        <el-form-item label="策略">
          <el-checkbox v-model="form.apply_to_all">全员合成：每段音频对所有已选形象各合成一次</el-checkbox>
        </el-form-item>

        <!-- 协议 -->
        <el-form-item>
          <el-checkbox v-model="agree" :disabled="!agreeEnabled">
            我已阅读并同意
            <el-link type="primary" :underline="false" @click.prevent="openAgreement">《克隆使用协议》</el-link>
          </el-checkbox>
          <span v-if="agreeCountdown > 0" class="muted ml8">（请至少阅读 {{ agreeCountdown }} 秒）</span>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" :disabled="!agreeReady || !canSubmit" @click="submit">
          开始合成（预计 {{ totalCost }} 积分 / {{ totalSeconds }} 秒）
        </el-button>
      </template>
    </el-dialog>

    <!-- 协议弹窗 -->
    <el-dialog v-model="agreementDialogVisible" :title="agreementTitle || '克隆使用协议'" width="720px"
      :close-on-click-modal="false" @closed="stopAgreeCountdown">
      <div v-loading="agreementLoading" style="min-height: 200px;">
        <div v-if="agreementUpdatedAt" class="muted small mb8">最近更新：{{ agreementUpdatedAt }}</div>
        <div class="agreement-content" v-html="agreementContent"></div>
      </div>
      <template #footer>
        <el-button @click="agreementDialogVisible = false">关闭</el-button>
        <el-button type="primary" :disabled="agreeCountdown > 0" @click="acceptAgreement">
          已阅读（{{ agreeCountdown > 0 ? agreeCountdown + 's' : '可确认' }}）
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import Cookies from 'js-cookie'
import config from '@/config.js'
import { saveAs } from 'file-saver'
import { Plus, Refresh, Search, Delete, Upload } from '@element-plus/icons-vue'


import { getUploadSignature, uploadToOSS, beforeUpload, getVideoDuration } from '@/utils/uploadUtils'

export default {
  name: 'HumanVideoRecord',
  components: { Plus, Refresh, Search, Delete, Upload },
  data() {
    return {
      loading: false,
      list: [],
      pager: { page: 1, pageSize: 10, total: 0 },
      search: { keyword: '' },
      selectedIds: [],

      // 形象 & 历史音频
      figures: [],
      historyAudios: [],

      // 弹窗
      dialogVisible: false,
      submitting: false,

      refreshingBatch: false,
      rowRefreshingId: null,
      preview: {
        visible: false,
        row: null
      },
      // 协议
      agree: false,
      agreeEnabled: false,
      agreeCountdown: 0,
      agreeTimer: null,
      agreementDialogVisible: false,
      agreementLoading: false,
      agreementTitle: '',
      agreementUpdatedAt: '',
      agreementContent: '',
      zipMaking: false,
      zipProgress: 0,

      // 表单
      form: {
        human_task_name: '',
        human_clone_id: [],     // 多选 scene_task_id
        voice_type: 1,          // 1 本地/直链；2 历史
        generate_channel: 1,    // 1/2/3
        apply_to_all: false
      },

      // 本地/直链
      manualVoiceUrls: '',
      ossVoiceUrls: [],                 // 上传成功的url
      uploadQueue: [],
      processedUids: new Set(),
      durMap: {},                       // url -> duration_ms

      // 历史音频选择
      selectedHistoryIds: [],           // 仅存id，提交时映射出 url

      // 单价（每秒）
      price: { normal: 0, pro: 0, turbo: 0 }
    }
  },
  computed: {
    userInfo() { return JSON.parse(Cookies.get('user_info') || '{}') },

    pendingCount() {
      return (this.list || []).filter(x => Number(x.status) === 3 && x.video_task_id).length
    },
    voiceUrlList() {
      const manual = (this.manualVoiceUrls || '')
        .split(/[,\n]/g)
        .map(s => s.trim())
        .filter(Boolean)
      return Array.from(new Set([...manual, ...this.ossVoiceUrls]))
    },
    finalVoiceSet() {
      if (this.form.voice_type === 1) {
        return this.voiceUrlList.map(u => ({ id: null, url: u, duration_ms: this.durMap[u] || null }))
      } else {
        return this.selectedHistoryIds
          .map(id => {
            const rec = this.historyAudios.find(x => x.id === id)
            if (!rec) return null
            const url = rec.human_voice_synthetic_url || ''
            return { id: rec.id, url, duration_ms: this.durMap[url] || null }
          })
          .filter(Boolean)
      }
    },
    unitForChannel() {
      return this.form.generate_channel === 2 ? this.price.pro : (this.form.generate_channel === 3 ? this.price.turbo : this.price.normal)
    },
    baseSeconds() {
      return this.finalVoiceSet.reduce((s, v) => {
        const ms = v.duration_ms
        if (!ms || ms <= 0) return s
        return s + Math.max(1, Math.ceil(ms / 1000))
      }, 0)
    },
    totalSeconds() {
      const base = this.baseSeconds
      return this.form.apply_to_all ? base * Math.max(1, this.form.human_clone_id.length) : base
    },
    totalCost() { return this.totalSeconds * this.unitForChannel },
    priceTip() {
      const u = this.unitForChannel
      const note = this.form.apply_to_all
        ? `（全员合成：可估秒数 × 形象数 ${Math.max(1, this.form.human_clone_id.length)}）`
        : ''
      return `当前通道单价：${u} 积分/秒；可估：${this.totalSeconds}s × ${u} = ${this.totalCost} 积分 ${note}。历史音频因无法预先获取时长，实际扣点以平台返回为准。`
    },
    agreeReady() { return this.agree && this.agreeCountdown === 0 },
    canSubmit() {
      const hasFigures = this.form.human_clone_id.length > 0
      const hasAudio = this.form.voice_type === 1 ? this.voiceUrlList.length > 0 : this.selectedHistoryIds.length > 0
      return hasFigures && hasAudio
    }
  },

  props: {
    cloneId: {
      type: String,
      default: ''
    }
  },

  watch: {
    async selectedHistoryIds(newIds) {
      const urls = newIds
        .map(id => this.historyAudios.find(x => x.id === id))
        .filter(Boolean)
        .map(r => r.human_voice_synthetic_url)
        .filter(Boolean)
      for (const u of urls) {
        if (!this.durMap[u]) {
          await this.fetchDurationForUrl(u)
        }
      }
    },
    cloneId: {
      immediate: true,
      handler(newId) {
        if (newId) {
          // 延迟执行以确保组件已完全加载
          this.$nextTick(() => {
            setTimeout(() => {
              this.openAddWithCloneId(newId);
            }, 500);
          });
        }
      }
    }

  },
  mounted() {
    this.fetchList()
    this.fetchFigures()
    this.fetchHistoryAudios()

    this._autoTimer = setInterval(() => {
      if (this.pendingCount > 0 && !this.refreshingBatch) {
        this.refreshPending()
      }
    }, 15000) // 每15秒
  },
  beforeUnmount() {
    if (this._autoTimer) clearInterval(this._autoTimer)
  },

  methods: {
    channelLabel(v) { return v === 2 ? 'PRO' : (v === 3 ? '极速' : '普通') },
    channelTagType(v) { return v === 2 ? 'warning' : (v === 3 ? 'danger' : '') },

    openPreview(row) {
      this.preview.row = row
      this.preview.visible = true
    },

    // 使用形象ID打开创建对话框
    openAddWithCloneId(cloneId) {
      this.openAdd(); // 打开对话框

      // 延迟选择形象，确保选项已加载
      setTimeout(() => {
        if (this.figures.some(fig => fig.scene_task_id === cloneId)) {
          this.form.human_clone_id = [cloneId];
          this.$message.success(`已自动选择形象: ${cloneId}`);
        } else {
          this.$message.warning(`未找到ID为 ${cloneId} 的形象`);
        }
      }, 800);
    },

    async downloadBatchZip() {
      const rows = this.list
        .filter(r => this.selectedIds.includes(r.id))
        .filter(r => r.human_synthetic_video_url)

      if (!rows.length) {
        this.$message.warning('请选择至少一个“已合成”的视频')
        return
      }

      this.zipMaking = true
      try {
        const items = rows.map(r => ({
          url: r.human_synthetic_video_url,
          filename: this.buildSafeName(`${r.human_task_name || 'video'}_${r.id}.mp4`)
        }))

        const res = await fetch(`${config.BASE_URL}/shop/api/zip_videos.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        })

        if (!res.ok) throw new Error(`HTTP ${res.status}`)

        const blob = await res.blob()
        saveAs(blob, `videos_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '')}.zip`)
        this.$message.success(`打包完成，共 ${rows.length} 个视频`)
      } catch (e) {
        console.error(e)
        this.$message.error('打包失败，请重试')
      } finally {
        this.zipMaking = false
        this.zipProgress = 0
      }
    },


    // 抓取远端文件为 Blob（注意：若视频URL不允许跨域，将报错）
    async fetchAsBlob(url) {
      // 用 fetch 保留文件名/类型，且能拿到 blob
      const res = await fetch(url, { mode: 'cors' }) // 如跨域受限，可改方案B走后端
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return await res.blob()
    },

    // 简单的文件名清洗
    buildSafeName(name) {
      return String(name)
        .replace(/[\\/:*?"<>|]/g, '_')
        .replace(/\s+/g, '_')
        .slice(0, 120) // 防止过长
    },

    // 单个下载（之前的普通下载仍可保留）
    downloadOne(row) {
      if (!row?.human_synthetic_video_url) {
        this.$message.warning('没有可下载的视频')
        return
      }
      this.forceDownload(row.human_synthetic_video_url, `${row.human_task_name || 'video'}_${row.id}.mp4`)
    },

    forceDownload(url, filename) {
      const a = document.createElement('a')
      a.href = url
      a.download = filename || 'video.mp4'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    },
    // 批量刷新正在进行中的任务
    async refreshPending() {
      try {
        this.refreshingBatch = true
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/human_clone_synth_task_query.php`,
          {
            action: 'refresh_pending',
            store_id: this.userInfo.id,
            limit: 100 // 可调
          }
        )
        if (data?.success) {
          const updated = data.updated || 0
          if (updated > 0) {
            this.$message.success(`已刷新：${updated} 条`)
          } else {
            this.$message.info('暂无可更新的任务')
          }
          // 刷新列表
          await this.fetchList()
        } else {
          this.$message.error(data?.error || '批量刷新失败')
        }
      } catch (e) {
        this.$message.error('批量刷新失败')
      } finally {
        this.refreshingBatch = false
      }
    },

    // 刷新单条（仅对 status=3 的行显示按钮）
    async refreshOne(row) {
      if (!row?.video_task_id) return
      try {
        this.rowRefreshingId = row.id
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/human_clone_synth_task_query.php`,
          {
            action: 'query_one',
            store_id: this.userInfo.id,
            video_task_id: String(row.video_task_id)
          }
        )
        if (data?.success) {
          this.$message.success('已刷新该任务')
          await this.fetchList()
        } else {
          this.$message.error(data?.error || '刷新失败')
        }
      } catch (e) {
        this.$message.error('刷新失败')
      } finally {
        this.rowRefreshingId = null
      }
    },

    // ---------- 列表 ----------
    async fetchList() {
      try {
        this.loading = true
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/human_clone_synthetic_record.php`, {
          action: this.search.keyword ? 'search' : 'list',
          store_id: this.userInfo.id,
          keyword: this.search.keyword,
          page: this.pager.page,
          page_size: this.pager.pageSize
        })
        this.list = data.data || []
        this.pager.total = data.pagination ? data.pagination.total : (this.list?.length || 0)
        if (!this.list.length && this.pager.page > 1) {
          this.pager.page = this.pager.page - 1
          await this.fetchList()
        }
        this.selectedIds = []
      } catch { this.$message.error('获取列表失败') }
      finally { this.loading = false }
    },
    doSearch() { this.pager.page = 1; this.fetchList() },
    handleSelectionChange(rows) { this.selectedIds = rows.map(r => r.id) },
    async doBatchDelete() {
      if (!this.selectedIds.length) return
      try {
        this.loading = true
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/human_clone_synthetic_record.php`, {
          action: 'batch_delete', store_id: this.userInfo.id, ids: this.selectedIds
        })
        if (data.success) { this.$message.success('批量删除成功'); await this.fetchList() }
        else { this.$message.error(data.error || '批量删除失败') }
      } catch { this.$message.error('批量删除失败') }
      finally { this.loading = false }
    },
    async delOne(id) {
      try {
        await this.$confirm('确定删除该记录吗？', '提示', { type: 'warning' })
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/human_clone_synthetic_record.php`, {
          action: 'delete', id, store_id: this.userInfo.id
        })
        if (data.success) { this.$message.success('已删除'); this.fetchList() }
        else { this.$message.error(data.error || '删除失败') }
      } catch (e) { /* 用户取消/报错静默 */ }
    },

    // ---------- 数据源 ----------
    async fetchFigures() {
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/human_clone.php`, {
          action: 'list_all', store_id: this.userInfo.id
        })
        const rows = data?.data || []
        this.figures = rows
          .filter(r => r.scene_task_id)
          .map(r => ({ scene_task_id: r.scene_task_id, human_name: r.human_name || r.scene_task_id }))
      } catch { this.figures = [] }
    },
    async fetchHistoryAudios() {
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/voice_clone_synthetic_record.php`, {
          action: 'list_all', store_id: this.userInfo.id
        })
        this.historyAudios = data?.data || []

        // ✅ 新增：预取前 20 条的时长
        const firstBatch = this.historyAudios.slice(0, 20)
        for (const r of firstBatch) {
          const url = r?.human_voice_synthetic_url
          if (url && !this.durMap[url]) {
            await this.fetchDurationForUrl(url)
          }
        }
      } catch {
        this.historyAudios = []
      }
    },


    // ---------- 弹窗 ----------
    async openAdd() {
      this.dialogVisible = true
      // 协议复位
      this.agree = false; this.agreeEnabled = false; this.stopAgreeCountdown()
      // 表单复位
      this.form = { human_task_name: '', human_clone_id: [], voice_type: 1, generate_channel: 1, apply_to_all: false }
      this.manualVoiceUrls = ''; this.ossVoiceUrls = []; this.uploadQueue = []; this.processedUids.clear()
      this.durMap = {}; this.selectedHistoryIds = []
      this.$nextTick(() => this.$refs.formRef?.clearValidate?.())

      // 拉配置价（每秒）
      try {
        const { data } = await axios.get(`${config.BASE_URL}/shop/api/get_digital_human_config.php`)
        const n = Number(parseFloat(data?.data?.digital_human_normal_synth_num || 0).toFixed(2))
        const p = Number(parseFloat(data?.data?.digital_human_pro_synth_num || 0).toFixed(2))
        const t = Number(parseFloat(data?.data?.digital_human_turbo_synth_num || 0).toFixed(2))
        this.price = {
          normal: isNaN(n) ? 0 : n,
          pro: isNaN(p) ? 0 : p,
          turbo: isNaN(t) ? 0 : t
        }
      } catch { this.price = { normal: 0, pro: 0, turbo: 0 } }
    },

    // ---------- 协议 ----------
    async openAgreement() {
      try {
        this.agreementDialogVisible = true
        this.agreementLoading = true
        const { data } = await axios.get(`${config.BASE_URL}/shop/api/get_clone_agreement.php`)
        if (data?.code === 200 && data?.data) {
          this.agreementTitle = data.data.title || '克隆使用协议'
          this.agreementUpdatedAt = data.data.updated_at || ''
          this.agreementContent = data.data.content || ''
        } else {
          this.agreementTitle = '克隆使用协议'
          this.agreementUpdatedAt = ''
          this.agreementContent = ''
        }
      } catch { this.$message.error('读取使用协议失败'); this.agreementContent = '' }
      finally { this.agreementLoading = false; this.startAgreeCountdown(3) }
    },
    startAgreeCountdown(sec) {
      this.stopAgreeCountdown()
      this.agreeCountdown = sec
      this.agreeTimer = setInterval(() => {
        if (this.agreeCountdown > 0) this.agreeCountdown--
        if (this.agreeCountdown <= 0) this.stopAgreeCountdown()
      }, 1000)
    },
    stopAgreeCountdown() { if (this.agreeTimer) clearInterval(this.agreeTimer); this.agreeTimer = null },
    acceptAgreement() { if (this.agreeCountdown > 0) return; this.agreementDialogVisible = false; this.agreeEnabled = true; this.agree = true },

    // ---------- 工具 ----------
    insertComma(field) {
      const v = (this.form[field] || '')
      const ends = /[，,]\s*$/.test(v)
      this.form[field] = ends ? v + ' ' : (v ? v + ', ' : ', ')
    },
    copyText(text) {
      if (!text) return
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(() => this.$message.success('已复制'))
      } else {
        const ta = document.createElement('textarea'); ta.value = text; ta.style.position = 'fixed'; ta.style.left = '-9999px'
        document.body.appendChild(ta); ta.select()
        try { document.execCommand('copy'); this.$message.success('已复制') } catch { this.$message.error('复制失败') }
        finally { document.body.removeChild(ta) }
      }
    },
    findHistoryLabel(id) {
      const r = this.historyAudios.find(x => x.id === id)
      return r ? `${r.voice_task_name || '未命名'}（#${r.id}）` : `#${id}`
    },

    // 根据 url 显示“向上取整秒数”或占位符
    durDisplay(url) {
      if (!url) return '—'
      const ms = this.durMap[url]
      return ms && ms > 0 ? `${Math.ceil(ms / 1000)}s` : '—'
    },

    // 通过选中的历史记录 id 找到对应的音频 url
    historyUrlById(id) {
      const r = this.historyAudios.find(x => x.id === id)
      return r?.human_voice_synthetic_url || ''
    },


    // 拉取音频时长（向上取整秒）
    // 已有：拉取音频时长（保留你之前实现的向上取整逻辑）
    async fetchDurationForUrl(url) {
      if (!url) return
      try {
        const api = `https://szr-api.aijzdi.com/auth/wyjuzhen/audio_duration.php?audio_url=${encodeURIComponent(url)}`
        const { data } = await axios.get(api)
        if (data?.success && typeof data.duration_seconds === 'number') {
          const secs = Math.ceil(data.duration_seconds) // 向上取整
          const ms = secs * 1000
          this.durMap = { ...this.durMap, [url]: ms }
        }
      } catch {
        // 静默失败即可
      }
    },

    // ---------- 本地上传 ----------
    async onPickLocalAudio(uploadFile) {
      try {
        const uid = uploadFile?.uid
        const file = uploadFile?.raw || uploadFile
        if (!file) return
        if (uid && this.processedUids.has(uid)) return
        if (uid) this.processedUids.add(uid)

        const q = { id: uid || `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`, name: file.name || '未命名', percent: 0, status: undefined }
        this.uploadQueue.push(q)

        const ok = await beforeUpload(file, { type: 'audio' })
        if (!ok) { q.status = 'exception'; return }
        q.percent = 10

        // 读取时长（ms）
        try {
          const secs = await getVideoDuration(file)
          const ms = isNaN(secs) ? null : Math.round(secs * 1000)
          if (ms != null && ms < 1000) this.$message.warning(`${q.name} 时长较短（≈${Math.ceil(ms / 1000)}s）`)
          if (ms != null) q.ms = ms
        } catch (e) { /* 静默 */ }

        q.percent = 45

        // 上传 OSS
        const sign = await getUploadSignature('audio')
        const res = await uploadToOSS(file, sign)
        q.percent = 90

        if (res?.success && res.url) {
          // 保存 & 时长
          this.ossVoiceUrls.push(res.url)
          if (q.ms != null) this.durMap = { ...this.durMap, [res.url]: q.ms }
          q.percent = 100; q.status = 'success'
          this.$message.success(`上传成功：${q.name}`)

          // 同步回写“音频直链”输入（按换行去重展示）
          const manualParts = (this.manualVoiceUrls || '').split(/[,\n]/g).map(s => s.trim()).filter(Boolean)
          const all = Array.from(new Set([...manualParts, ...this.ossVoiceUrls]))
          this.manualVoiceUrls = all.join('\n')
        } else {
          q.status = 'exception'
          this.$message.error(`上传失败：${q.name}`)
        }
      } catch (err) {
        this.$message.error(err?.message || '上传失败')
      }
    },

    // ✅ 修正 no-unused-vars：避免解构出未使用变量
    removeVoiceUrl(i) {
      const url = this.voiceUrlList[i]
      // 从上传集合移除
      this.ossVoiceUrls = this.ossVoiceUrls.filter(u => u !== url)
      // 重建多行输入框（换行）
      this.manualVoiceUrls = this.voiceUrlList.filter(u => u !== url).join('\n')
      // 删除 durMap 对应项（无未用变量）
      const next = { ...this.durMap }
      delete next[url]
      this.durMap = next
    },

    removeHistoryId(id) { this.selectedHistoryIds = this.selectedHistoryIds.filter(x => x !== id) },

    // ---------- 提交 ----------
    async submit() {
      this.$refs.formRef.validate(async (ok) => {
        if (!ok) return
        if (!this.agreeReady) { this.$message.warning('请先阅读并同意克隆使用协议'); return }
        if (!this.canSubmit) { this.$message.warning('请选择形象和音频'); return }

        this.submitting = true
        try {
          // 任务名拆分
          const nameList = (this.form.human_task_name || '').split(/[，,]/g).map(s => s.trim()).filter(Boolean)
          const taskNamePayload = nameList.length > 1 ? nameList : (nameList[0] || '')

          // 音频payload
          const voice_type = this.form.voice_type
          let voice_url = []
          let history_voice_id = []
          let duration_ms_list = []

          if (voice_type === 1) {
            voice_url = this.finalVoiceSet.map(x => x.url)
            duration_ms_list = this.finalVoiceSet.map(x => x.duration_ms || null)
          } else {
            history_voice_id = this.finalVoiceSet.map(x => x.id)
            voice_url = this.finalVoiceSet.map(x => x.url)
            duration_ms_list = this.finalVoiceSet.map(x => x.duration_ms || null)
          }

          const payload = {
            action: 'add',
            store_id: this.userInfo.id,
            human_task_name: taskNamePayload,
            human_clone_id: this.form.human_clone_id.length > 1 ? this.form.human_clone_id : (this.form.human_clone_id[0] || ''),
            voice_type,
            voice_url: voice_url.length > 1 ? voice_url : (voice_url[0] || ''),
            history_voice_id: history_voice_id.length > 1 ? history_voice_id : (history_voice_id[0] || ''),
            duration_ms: duration_ms_list.length > 1 ? duration_ms_list : (duration_ms_list[0] || null),
            generate_channel: this.form.generate_channel,
            apply_to_all: this.form.apply_to_all,
            callback_url: 'https://www.baidu.com'
          }

          const { data } = await axios.post(`${config.BASE_URL}/shop/api/human_clone_synthetic_record.php`, payload)
          if (data.success) {
            this.$message.success('已提交视频合成任务')
            this.dialogVisible = false
            this.fetchList()
          } else {
            this.$message.error(data.error || '提交失败')
          }
        } catch { this.$message.error('提交失败') }
        finally { this.submitting = false }
      })
    }
  }
}
</script>

<style scoped>
.page-container {
  padding: 12px 16px;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.ml10 {
  margin-left: 10px
}

.ml8 {
  margin-left: 8px
}

.mt6 {
  margin-top: 6px
}

.muted {
  color: #9ca3af
}

.small {
  font-size: 12px
}

.mb8 {
  margin-bottom: 8px
}

.pager {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
}

.inline-actions {
  margin-top: 8px;
}

.hint {
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
}

.drop-audio {
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  background: #fff;
  transition: border-color .2s, box-shadow .2s;
  margin-top: 8px;
}

.drop-audio:hover {
  border-color: #93c5fd;
  box-shadow: 0 8px 24px rgba(59, 130, 246, .08);
}

.drop-inner {
  padding: 18px 12px;
  text-align: center;
  color: #6b7280;
}

.drop-title {
  font-weight: 600;
  color: #1f2937;
}

.drop-sub .link {
  color: #2563eb;
  cursor: pointer;
}

.url-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.queue {
  margin-top: 10px;
  max-height: 220px;
  overflow-y: auto;
}

.queue-title {
  font-weight: 600;
  margin-bottom: 6px;
  color: #374151;
}

.queue-item {
  margin-bottom: 10px;
}

.q-name {
  display: block;
  font-size: 12px;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 4px;
}

/* 列表里音频/视频行整体水平垂直居中 */
.audio-cell,
.video-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.audio-cell audio {
  width: 240px;
  max-width: 100%;
}

.video-cell video {
  width: 260px;
  max-width: 100%;
}

.agreement-content {
  max-height: 60vh;
  overflow-y: auto;
  white-space: pre-wrap;
  line-height: 1.7;
}

.opt-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.opt-name {
  font-weight: 600;
}

.opt-sub {
  color: #94a3b8;
  font-size: 12px;
}

.mr8 {
  margin-right: 8px;
}

/* 视频缩略图 + 操作的轻量布局 */
.video-cell-lite {
  display: flex;
  align-items: center;
  gap: 10px;
}

.thumb {
  width: 120px;
  height: 68px;
  /* 16:9 近似 */
  border-radius: 6px;
  overflow: hidden;
  flex: 0 0 auto;
  background: #f3f4f6;
}

.thumb--placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 12px;
}

.v-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 列表里音频控件尽量小，避免拉高行高 */
.audio-el {
  width: 200px;
  max-width: 100%;
}

/* 预览弹窗内的视频尺寸独立，不影响行高 */
.preview-wrap {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.preview-video {
  width: 100%;
  max-height: 60vh;
  border-radius: 8px;
  background: #000;
}

.preview-meta {
  font-size: 12px;
  color: #6b7280;
  display: flex;
  gap: 12px;
}
</style>
